'use client'

import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type { Database } from '@/lib/supabase/database'
import { createClient } from '@/lib/supabase/client-component'
import { getLocaleCookieClient, setLocaleCookieClient } from '@/lib/i18n/locale-cookie'
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/lib/i18n/types'

type UserProfile = Database['public']['Tables']['user_profiles']['Row']

interface User {
  id: string
  email: string
}

interface Session {
  user: User
  access_token: string
}

interface WorkingAuthContextType {
  user: User | null
  profile: UserProfile | null
  session: Session | null
  loading: boolean
  sessionExpired: boolean
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
  handleAuthError: (error: any, context?: string) => Promise<void>
  reAuthenticate: (accessToken: string, userData: { id: string; email: string }) => void
}

const WorkingAuthContext = createContext<WorkingAuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(WorkingAuthContext)
  if (context === undefined) {
    // Instead of throwing an error, return a safe default state
    console.error('useAuth must be used within a WorkingAuthProvider')
    return {
      user: null,
      profile: null,
      session: null,
      loading: true,
      sessionExpired: false,
      signOut: async () => {},
      refreshProfile: async () => {},
      handleAuthError: async () => {},
      reAuthenticate: () => {}
    }
  }
  return context
}

interface WorkingAuthProviderProps {
  children: React.ReactNode
}

export function WorkingAuthProvider({ children }: WorkingAuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  // Stored in a ref (not state): avoids re-renders on start/stop and ensures the
  // unmount cleanup always reads the current timer (state closure would be stale → orphaned timers, D7-14).
  const sessionMonitorIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [sessionExpired, setSessionExpired] = useState(false)

  const fetchProfile = async (userId: string) => {
    try {
      // Read the profile through the Supabase SDK over the cookie session (D7-10).
      // RLS scopes the row to the authenticated user — no manual token threading
      // or hand-built REST URL needed. maybeSingle() returns null (not an error)
      // when the profile row doesn't exist yet.
      const supabase = createClient()
      const { data, error } = await (supabase as any)
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      if (error) {
        console.error('Profile fetch failed:', error.message)
        return null
      }

      return data ?? null
    } catch (error) {
      console.error('Profile fetch error:', error)
      return null
    }
  }

  const handleAuthError = async (error: any, context?: string) => {
    console.warn(`Auth error in ${context || 'unknown context'}:`, error)
    
    // Check if it's an auth-related error. Primary signal is the HTTP status
    // (401/403); message matching is intentionally narrow so that unrelated
    // errors like "invalid input syntax" / "invalid date" don't trigger an
    // unexpected logout (D7-13).
    const message = String(error?.message || '').toLowerCase()
    const isAuthError = (
      error?.status === 401 ||
      error?.status === 403 ||
      error?.code === 401 ||
      error?.code === 403 ||
      message.includes('jwt') ||
      message.includes('expired') ||
      message.includes('unauthorized') ||
      message.includes('invalid token') ||
      message.includes('invalid jwt') ||
      message.includes('invalid_grant') ||
      message.includes('invalid claim') ||
      message.includes('invalid refresh')
    )

    if (isAuthError) {
      await signOut({ showAuthModal: true })
    }
  }

  const refreshProfile = async () => {
    if (user) {
      // Pull a fresh access token from the cookie session — the SDK auto-refreshes
      // it, so we must not rely on the (possibly stale) token captured in state.
      const { data: { session: freshSession } } = await createClient().auth.getSession()
      const accessToken = freshSession?.access_token
      if (!accessToken) {
        await handleAuthError({ status: 401 }, 'profile refresh')
        return
      }

      // First check and handle evaluation expiry
      try {
        // Server route (service role) – the RPC itself is not executable by authenticated users
        const expiryResponse = await fetch('/api/subscription/check-evaluation-expiry', { method: 'POST' })

        if (expiryResponse.ok) {
          // Evaluation→Free downgrade is applied server-side; no client action needed
          await expiryResponse.json()
        } else if (expiryResponse.status === 401) {
          await handleAuthError({ status: expiryResponse.status }, 'evaluation expiry check')
          return
        } else {
          console.warn('Auth: Could not check evaluation expiry:', expiryResponse.status)
        }
      } catch (error) {
        console.warn('Auth: Evaluation expiry check failed:', error)
        // Don't trigger auth error for network issues
      }

      // Then fetch the updated profile
      try {
        const profile = await fetchProfile(user.id)
        setProfile(profile)
      } catch (error) {
        await handleAuthError(error, 'profile refresh')
      }
    }
  }

  const startSessionMonitoring = () => {
    // Clear any existing interval
    if (sessionMonitorIntervalRef.current) {
      clearInterval(sessionMonitorIntervalRef.current)
    }

    // Check session every 10 minutes
    const interval = setInterval(async () => {
      try {
        const response = await fetch('/api/auth/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
        
        const result = await response.json()
        
        if (!result.valid) {
          console.warn('Auth: Periodic session check failed, logging out:', result.error)
          await signOut({ showAuthModal: true })
        } else if (result.expires_in_minutes < 5) {
          console.warn(`Auth: Session expires in ${result.expires_in_minutes} minutes`)
          // Could show a warning notification here
        }
      } catch (error) {
        console.warn('Auth: Periodic session check network error:', error)
        // Don't logout on network errors, just log the warning
      }
    }, 10 * 60 * 1000) // 10 minutes

    sessionMonitorIntervalRef.current = interval
  }

  const stopSessionMonitoring = () => {
    if (sessionMonitorIntervalRef.current) {
      clearInterval(sessionMonitorIntervalRef.current)
      sessionMonitorIntervalRef.current = null
    }
  }

  const signOut = async (options: { showAuthModal?: boolean } = {}) => {
    const { showAuthModal = false } = options

    // Prevent duplicate signOut calls
    if (isSigningOut) {
      return
    }

    setIsSigningOut(true)

    try {
      // Stop session monitoring
      stopSessionMonitoring()

      // Clear the cookie-based session (single source of truth)
      try {
        await createClient().auth.signOut()
      } catch (e) {
        console.warn('Supabase signOut failed:', e)
      }

      // Legacy cleanup: remove the old custom localStorage blob from pre-cookie
      // builds, plus any stale sb-* keys, so an old session can't linger.
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem('supabase_auth')
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i)
            if (key && key.startsWith('sb-')) {
              localStorage.removeItem(key)
            }
          }
        } catch (e) {
          console.warn('Could not clear stale auth localStorage keys:', e)
        }
      }

      setUser(null)
      setProfile(null)
      setSession(null)

      if (typeof window !== 'undefined') {
        sessionStorage.clear()

        if (showAuthModal && window.location.pathname.startsWith('/dashboard')) {
          // Token expired while on dashboard - show modal overlay instead of redirecting
          setSessionExpired(true)
        } else if (showAuthModal) {
          // Token expired on non-dashboard page - redirect to homepage with login popup
          window.location.replace('/?auth=open')
        } else {
          // Manual logout - just go to homepage
          window.location.replace('/')
        }
      }
    } catch (error) {
      console.error('Sign out error:', error)
      if (typeof window !== 'undefined') {
        sessionStorage.clear()
        if (showAuthModal && window.location.pathname.startsWith('/dashboard')) {
          setSessionExpired(true)
        } else {
          window.location.replace(showAuthModal ? '/?auth=open' : '/')
        }
      }
    } finally {
      // Reset signing out state after a delay to prevent race conditions
      setTimeout(() => {
        setIsSigningOut(false)
      }, 1000)
    }
  }

  const reAuthenticate = (accessToken: string, userData: { id: string; email: string }) => {
    const newUser = { id: userData.id, email: userData.email }
    const newSession = { user: newUser, access_token: accessToken }
    setUser(newUser)
    setSession(newSession)
    setSessionExpired(false)
    // Restart session monitoring
    startSessionMonitoring()
    // Refresh profile
    fetchProfile(userData.id).then(p => {
      if (p) setProfile(p)
    })
  }

  useEffect(() => {
    const loadAuth = async () => {
      try {
        setLoading(true)

        // Stop any existing session monitor from previous session
        // This prevents stale monitors from running after auth expiry
        stopSessionMonitoring()

        // Only run on client side
        if (typeof window === 'undefined') {
          setLoading(false)
          return
        }

        const supabase = createClient()

        // getUser() verifies the JWT against the auth server and auto-refreshes
        // via the cookie-stored refresh token when needed. The cookie session
        // (managed by @supabase/ssr) is the single source of truth — no tokens
        // are read from localStorage anymore (D7-05).
        const { data: { user: authUser }, error: userError } = await supabase.auth.getUser()

        let currentUser: User | null = null
        let accessToken: string | undefined

        if (authUser) {
          // Access token is needed for the authenticated REST profile fetch below.
          const { data: { session: authSession } } = await supabase.auth.getSession()
          accessToken = authSession?.access_token
          if (accessToken) {
            currentUser = { id: authUser.id, email: authUser.email || '' }
          }
        } else {
          // getUser() is authoritative. We NEVER hydrate auth state from an
          // unverified local session (fail closed — a forged/expired cookie that
          // the auth server would reject must not produce a logged-in client
          // state). The error shape only decides the UX, not whether to grant auth.
          const errName = (userError as any)?.name
          const status = (userError as any)?.status
          const isRetryable =
            errName === 'AuthRetryableFetchError' ||
            status === 0 ||
            status === 429 ||
            (typeof status === 'number' && status >= 500)
          const isSessionMissing = errName === 'AuthSessionMissingError'

          // A session existed but is no longer valid (refresh failed) and we're on
          // the dashboard → show the in-place re-auth modal instead of losing the
          // page. Retryable network errors and "no session" fall through: the
          // dashboard layout redirects to the login modal and the next reload
          // re-validates (we don't claim "expired" when we couldn't reach the server).
          if (!isRetryable && !isSessionMissing && window.location.pathname.startsWith('/dashboard')) {
            setSessionExpired(true)
          }
        }

        if (!currentUser || !accessToken) {
          setLoading(false)
          return
        }

        // Fetch the profile FIRST so we can reconcile the locale before committing
        // any UI state — otherwise the app paints in the stale locale for ~100ms
        // and then reloads (the D7-12 flash).
        const fetchedProfile = await fetchProfile(currentUser.id)

        const currentCookieLocale = getLocaleCookieClient()
        const dbLocale = (fetchedProfile as any)?.preferred_language as SupportedLocale | null

        // DB has a locale preference that differs from the cookie → fix the cookie
        // and reload NOW, before committing auth/profile state, so nothing renders
        // in the wrong locale. The reload re-enters this effect with a matching
        // cookie (no loop). Skipped on the auth-redirect page to avoid interrupting it.
        if (
          dbLocale &&
          SUPPORTED_LOCALES.includes(dbLocale) &&
          currentCookieLocale !== dbLocale &&
          !window.location.search.includes('auth=open')
        ) {
          setLocaleCookieClient(dbLocale)
          window.location.reload()
          return
        }

        // Commit auth + profile state.
        setUser(currentUser)
        setSession({ user: currentUser, access_token: accessToken })
        startSessionMonitoring()
        setProfile(fetchedProfile)

        // No DB preference but a cookie locale exists → persist it to the DB.
        if (
          (!dbLocale || !SUPPORTED_LOCALES.includes(dbLocale)) &&
          currentCookieLocale &&
          SUPPORTED_LOCALES.includes(currentCookieLocale)
        ) {
          try {
            await fetch('/api/account/update-language', {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ language: currentCookieLocale })
            })
          } catch (error) {
            console.warn('Auth: Failed to save locale preference to database:', error)
          }
        }

      } catch (error) {
        console.error('Auth loading failed:', error)
      } finally {
        setLoading(false)
      }
    }

    loadAuth()
  }, [])

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      stopSessionMonitoring()
    }
  }, [])

  return (
    <WorkingAuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        sessionExpired,
        signOut,
        refreshProfile,
        handleAuthError,
        reAuthenticate,
      }}
    >
      {children}
    </WorkingAuthContext.Provider>
  )
}